// pages/TakeTestPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import ErrorMessage from "../components/common/ErrorMessage";

const TakeTestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(1800); // Default 30 minutes
  const [testStarted, setTestStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTestData();
  }, [testId]);

  // Function to fetch test data
  const fetchTestData = async () => {
    try {
      setLoading(true);
      
      // Fetch the test details
      const testDoc = await getDoc(doc(db, "tests", testId));
      
      if (testDoc.exists()) {
        const testData = { id: testDoc.id, ...testDoc.data() };
        setTest(testData);
        
        // Set timer based on test duration
        if (testData.duration) {
          const durationMatch = testData.duration.match(/(\d+)/);
          if (durationMatch) {
            setTimeLeft(parseInt(durationMatch[1]) * 60);
          }
        }
        
        // Initialize answers object
        if (testData.questionsData) {
          const initialAnswers = {};
          testData.questionsData.forEach((_, index) => {
            initialAnswers[index] = null;
          });
          setAnswers(initialAnswers);
        }
      } else {
        setError("Test not found!");
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error fetching test data:", err);
      setError("Failed to load test. Please try again.");
      setLoading(false);
    }
  };

  // Timer logic
  useEffect(() => {
    if (!testStarted || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          // Auto-submit when time is up
          handleSubmitTest();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [testStarted, timeLeft]);

  // Format time to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start test handler
  const handleStartTest = () => {
    setTestStarted(true);
  };

  // Handle answer selection
  const handleAnswerSelect = (questionIndex, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  // Submit test handler
  const handleSubmitTest = () => {
    // Calculate results
    let correctAnswers = 0;
    let totalQuestions = test.questionsData ? test.questionsData.length : 0;
    
    if (test.questionsData) {
      test.questionsData.forEach((question, index) => {
        if (answers[index] === question.correctAnswer) {
          correctAnswers++;
        }
      });
    }
    
    // Store results in localStorage for the results page
    const results = {
      testId,
      testTitle: test.title,
      totalQuestions,
      correctAnswers,
      answers,
      submittedAt: new Date().toISOString(),
      timeSpent: (1800 - timeLeft) // time spent in seconds
    };
    
    localStorage.setItem(`testResults_${testId}`, JSON.stringify(results));
    
    // Navigate to results page
    navigate(`/test-results/${testId}`);
  };

  // Navigation functions
  const goToNextQuestion = () => {
    if (currentQuestion < (test.questionsData?.length - 1 || 0)) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-blue-600 font-medium">Loading test...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // If no questions data available, show error
  if (!test.questionsData || test.questionsData.length === 0) {
    return (
      <div className="min-h-screen pt-20 flex justify-center items-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Questions Available</h3>
          <p className="text-gray-600 mb-4">This test doesn't have any questions configured yet.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {!testStarted ? (
        // Test Instructions Screen
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden p-8"
            >
              <h1 className="text-3xl font-bold text-gray-900 mb-6">{test.title}</h1>
              
              <div className="mb-6">
                <div className="bg-blue-50 rounded-lg p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Overview</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Duration: {test.duration}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Questions: {test.questionsData?.length || test.questions}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span>Total Marks: {test.marks}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className={`
                        ${test.difficulty === "Easy" ? "text-green-600" : ""}
                        ${test.difficulty === "Medium" ? "text-yellow-600" : ""}
                        ${test.difficulty === "Hard" ? "text-red-600" : ""}
                      `}>
                        Difficulty: {test.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
              
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h2>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Once you start the test, the timer will begin. You cannot pause or restart the test once it has begun.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {test.instructions?.map((instruction, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{instruction}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Syllabus Coverage */}
                {test.syllabus && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Syllabus Coverage</h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {test.syllabus.map((topic, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="h-5 w-5 text-blue-500 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <span className="text-gray-700">{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-200 pt-6 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Go Back
                </button>
                <button
                  onClick={handleStartTest}
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm focus:outline-none"
                >
                  Start Test Now
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      ) : (
        // Test Taking Screen
        <section className="py-8 bg-gray-50 min-h-screen">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Test Header */}
            <div className="sticky top-20 z-10 bg-gray-50 pt-4 pb-2 mb-6">
              <div className="flex flex-col lg:flex-row justify-between items-center px-6 py-3 bg-white rounded-lg shadow">
                <h1 className="text-xl font-bold text-gray-900 mb-3 lg:mb-0">{test.title}</h1>
                
                <div className="flex space-x-4 items-center">
                  <div className="text-sm text-gray-600">
                    Question {currentQuestion + 1} of {test.questionsData.length}
                  </div>
                  <div className={`text-lg font-bold flex items-center ${timeLeft < 300 ? 'text-red-600' : 'text-blue-600'}`}>
                    <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formatTime(timeLeft)}
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm("Are you sure you want to submit your test? This action cannot be undone.")) {
                        handleSubmitTest();
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Submit Test
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Question Navigation Panel */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow p-4 sticky top-40">
                  <h3 className="font-semibold text-gray-900 mb-4">Question Navigation</h3>
                  <div className="grid grid-cols-5 gap-2 mb-4">
                    {test.questionsData.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToQuestion(index)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                          currentQuestion === index
                            ? 'bg-blue-600 text-white'
                            : answers[index] !== null
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 rounded mr-2"></div>
                      <span>Current</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-100 border border-green-300 rounded mr-2"></div>
                      <span>Answered</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded mr-2"></div>
                      <span>Unanswered</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Question */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-lg shadow p-6">
                  {test.questionsData[currentQuestion] && (
                    <div>
                      <div className="flex items-start mb-6">
                        <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full mr-3 mt-1">
                          Q{currentQuestion + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-lg font-medium text-gray-900 mb-4">
                            {test.questionsData[currentQuestion].questionText}
                          </p>
                          
                          <div className="space-y-3">
                            {test.questionsData[currentQuestion].options.map((option, optionIndex) => (
                              <label
                                key={optionIndex}
                                className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                  answers[currentQuestion] === optionIndex
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`question-${currentQuestion}`}
                                  value={optionIndex}
                                  checked={answers[currentQuestion] === optionIndex}
                                  onChange={() => handleAnswerSelect(currentQuestion, optionIndex)}
                                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <span className="ml-3 text-gray-700 font-medium mr-3">
                                  {String.fromCharCode(65 + optionIndex)}.
                                </span>
                                <span className="text-gray-700 flex-1">{option}</span>
                              </label>
                            ))}
                          </div>

                          {/* Question metadata */}
                          <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
                            <div className="flex items-center space-x-4">
                              <span>Marks: {test.questionsData[currentQuestion].marks}</span>
                              <span className={`
                                ${test.questionsData[currentQuestion].difficulty === "Easy" ? "text-green-600" : ""}
                                ${test.questionsData[currentQuestion].difficulty === "Medium" ? "text-yellow-600" : ""}
                                ${test.questionsData[currentQuestion].difficulty === "Hard" ? "text-red-600" : ""}
                              `}>
                                {test.questionsData[currentQuestion].difficulty}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Navigation buttons */}
                      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                        <button
                          onClick={goToPreviousQuestion}
                          disabled={currentQuestion === 0}
                          className={`px-4 py-2 rounded-md font-medium ${
                            currentQuestion === 0
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-600 text-white hover:bg-gray-700'
                          }`}
                        >
                          Previous
                        </button>

                        <div className="flex space-x-2">
                          {currentQuestion < test.questionsData.length - 1 ? (
                            <button
                              onClick={goToNextQuestion}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                            >
                              Next
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                if (window.confirm("Are you sure you want to submit your test? This action cannot be undone.")) {
                                  handleSubmitTest();
                                }
                              }}
                              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                            >
                              Submit Test
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TakeTestPage;